import React, { useEffect, useState, useRef } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { NavLink } from 'react-router-dom';
import Slider from 'react-slick';
import { InterfaceRoutes } from '../../interfaces/interfaceRoutes';
import { CasoExito, ImagenInicio, Industria, Solucion } from '../../interfaces/types';
import classNames from 'classnames';

import GascoContacto from '../../assets/images/img-contacto.jpg';
import GascoPodcast from '../../assets/images/podcast.png';

import CardSkeleton from '../../components/Skeleton/CardSkeleton';
import ImageSkeleton from '../../components/Skeleton/ImageSkeleton';

// Styles
import './home.css';
import {
  STRAPI_CDN_URL,
  getImagenesInicio,
  getSolucionesEnergeticas,
  getIndustrias,
  getCasosExitoDestacados
} from '../../config/services/strapi/strapi';

function Home(props: InterfaceRoutes) {
  const sliderCasosRef = useRef<Slider | null>(null);

  const [cargando, setCargando] = useState({
    imagenesInicio: true,
    soluciones: true,
    industrias: true,
    casosExito: true
  });

  const [imagenesInicio, setImagenesInicio] = useState<ImagenInicio[]>([]);
  const [soluciones, setSoluciones] = useState<Partial<Solucion>[]>([]);
  const [industrias, setIndustrias] = useState<Industria[]>([]);
  const [casosExito, setCasosExito] = useState<CasoExito[]>([]);
  
  const [actualCaso, setActualCaso] = useState(1);

  const isMobile = useIsMobile();

  const handleCasoAnterior = () => {
    if (sliderCasosRef.current) {
      let newCaso = actualCaso - 1;
      if (newCaso < 1) {
        newCaso = casosExito.length;
      }
      setActualCaso(newCaso);

      sliderCasosRef.current.slickGoTo(newCaso - 1);
    }
  };

  const handleCasoSiguiente = () => {
    if (sliderCasosRef.current) {
      sliderCasosRef.current.slickGoTo(actualCaso);

      let newCaso = actualCaso + 1;
      if (newCaso > casosExito.length) {
        newCaso = 1;
      }
      setActualCaso(newCaso);
    }
  };

  useEffect(() => {
    const fetchImagenes = async () => {
      const rawImagenes = await getImagenesInicio();
      const mappedImagenes = rawImagenes.map((imagen: any) => ({
        nombre: imagen.nombre,
        url: imagen.imagen.url,
        textoDestacado: imagen.texto_destacado,
        descripcion: imagen.descripcion,
        alt: imagen.descripcion_imagen
      }));

      setImagenesInicio(mappedImagenes);
      setCargando(prevState => ({ ...prevState, imagenesInicio: false }));
    };

    const fetchSoluciones = async () => {
      const rawSoluciones = await getSolucionesEnergeticas({
        visible: true
      });
      
      const mappedSoluciones: Partial<Solucion>[] = rawSoluciones.map((solucion: any) => ({
        id: solucion.id,
        beneficios: solucion.beneficios,
        caracteristicas: solucion.caracteristicas,
        descripcion: solucion.descripcion,
        externo: solucion.externo,
        icono: solucion.icono,
        imagenBeneficios: solucion.imagen_beneficios,
        imagenIndustria: solucion.imagen_industria,
        imagenProyectos: solucion.imagen_proyectos,
        industrias: solucion.industrias,
        nombre: solucion.nombre,
        portada: {
          url: solucion.portada.url
        },
        url: solucion.url,
      }));

      setSoluciones(mappedSoluciones);
      setCargando(prevState => ({ ...prevState, soluciones: false }));
    };

    const fetchIndustrias = async () => {
      const rawIndustrias: any[] | null = await getIndustrias();
      if (!rawIndustrias) {
        setIndustrias([]);
        return;
      }

      const mappedIndustrias: Industria[] = rawIndustrias?.map((industria: any) => ({
        id: industria.id,
        nombre: industria.nombre,
        nombreIndustria: industria.nombre_industria,
        icono: {
          url: industria.icono?.url,
        },
        logo: {
          url: industria.logo?.url
        },
        descripcion: industria.descripcion,
        beneficios: industria.beneficios,
        slug: `/industrias/${industria.slug}`,
        banner: {
          url: industria.banner?.url
        },
        imagenBeneficios: {
          url: `${STRAPI_CDN_URL}/${industria.imagen_beneficios?.hash}${industria.imagen_beneficios?.ext}`
        },
        soluciones: industria.soluciones
      }));

      setIndustrias(mappedIndustrias);
      setCargando(prevState => ({ ...prevState, industrias: false }));
    };

    const fetchCasosExito = async () => {
      const rawCasos = await getCasosExitoDestacados();
      const mappedCasos: CasoExito[] = rawCasos.map((caso: any) => ({
        id: caso.id,
        nombre: caso.nombre,
        provincia: caso.provincia,
        region: caso.region,
        portadaCarousel: {
          url: `${STRAPI_CDN_URL}/${caso.portada_carousel?.hash}${caso.portada_carousel?.ext}`
        },
        slug: caso.slug
      }));

      setCasosExito(mappedCasos);
      setCargando(prevState => ({ ...prevState, casosExito: false }));
    };

    fetchImagenes();
    fetchSoluciones();
    fetchIndustrias();
    fetchCasosExito();
  }, []);

  return (
    <main>
      {/* Carrousel */}
      <section className='max-w-full overflow-x-hidden relative pb-[45px] md:pb-[48px] h-full'>
        <Slider
          arrows={true}
          dots={false}
          infinite={imagenesInicio.length > 1}
          speed={500}
          slidesToShow={1}
          slidesToScroll={1}
          autoplay={true}
          autoplaySpeed={5000}
        >
          
          {
            cargando.imagenesInicio ?

              <div className='w-full h-full relative'>
                <ImageSkeleton className="w-full h-[474.891px] lg:h-[calc(100vh-19rem)]" />
                <div className='absolute inset-0 left-0 top-0 w-full h-full bg-black opacity-30'></div>
              </div>

              :

              imagenesInicio.map((imagen, index) => (
                <div key={index} className='w-full h-full relative'>
                  <img src={imagen.url} alt={imagen.alt} className='w-full max-w-full min-h-[606.891px] md:min-h-[521px] lg:min-h-[calc(100vh-19rem)] lg:max-h-[calc(100vh-19rem)] lg:h-full object-cover' />
                  <div className="absolute w-full h-full left-0 top-0 flex justify-center items-center px-[3.7rem] md:px-44 xl:px-0">
                    <h2 className='max-w-3xl w-full text-center text-white text-[1.375rem] md:!text-[32.4px] xl:!text-4xl !font-[400] md:!font-[300] xl:!font-[300] leading-7 md:leading-[38.88px] xl:leading-[2.75rem] pb-6 xl:pb-10'>
                      <span className='!font-bold text-white'>{imagen.textoDestacado}</span><br className='hidden md:block' />
                      {imagen.descripcion}
                    </h2>
                  </div>
                </div>
              ))
          }
        </Slider>
      </section>
      {/* Soluciones */}
      <section className='px-4 md:px-10 lg:px-36 xl:px-[175px] 2xl:px-[400px] pb-4 md:pb-10 lg:pb-20' id='home-soluciones'>
        <div className='pb-10'>
          <h1 className="!text-[32px] md:!text-[36px] lg:text-[2.5rem] xl:!text-[40px] font-bold text-center leading-[38.4px] md:leading-[43.2px] lg:leading-[48px] text-[var(--text-color-secondary)] !mx-0 !mt-0 !mb-0 md:!mb-[9px] lg:!mb-[10px] !p-0">Soluciones Energéticas para Negocios y Empresas</h1>
          <p className="text-justify py-[18px] text-[var(--text-color-primary)] text-[16px] md:text-[18px] lg:text-[1.125rem] xl:text-[20px] xl:font-[400] leading-6 md:leading-[25.2px] lg:leading-8 lg:py-[20px]">
            Gasco ofrece Soluciones Energéticas competitivas e innovadoras, que comprenden los distintos eslabones de la cadena de valor de tu negocio, contribuyendo al desarrollo sostenible de tu operación
          </p>
        </div>
        <div className="block md:hidden">
          <Slider
            dots={true}
            infinite={true}
            arrows={false}
            speed={500}
            slidesToShow={2}
            slidesToScroll={1}
            className='!w-full !h-full'
          >
            {soluciones.map((solucion, index) => (

              cargando.soluciones ?

                <div key={index} className="px-2">
                  <ImageSkeleton className="w-full h-[280px] rounded-lg overflow-hidden" />
                </div>

                :

                <>
                  {
                    solucion.externo && (<a key={index} href={solucion.url} target="_blank" rel="noopener noreferrer" className='w-full h-[215px] block z-10 group'>
                      <div className={classNames('h-full bg-cover relative', {
                        [`bg-[url(${solucion.portada?.url})]`]: solucion.nombre
                      })}>
                        <div className='absolute w-[calc(100%-2rem)] bottom-4 left-4 bg-[var(--background-color-primary)] z-20 flex justify-between items-end p-2 min-h-[65px]'>
                          <div className='!text-white h-full min-h-[50px]'>
                            {solucion.nombre}
                          </div>
                          <div className='flex flex-col justify-end'>
                            <button className='!rounded-full !border !border-white w-5 h-5 flex justify-center items-center !text-white group-hover:bg-white group-hover:!text-[var(--text-color-primary)] transition duration-500 ease-initial'>
                              <i className='fas fa-chevron-right text-[4px] font-[100]'></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </a>)
                  }

                  {
                    !solucion.externo && <div key={index} className="px-2">
                      <NavLink to={`/soluciones/${solucion.url}`} className='w-full h-[215px] block z-10 group'>
                        <div className={classNames('h-full bg-cover relative', {
                          [`bg-[url(${solucion.portada?.url})]`]: solucion.nombre
                        })}>
                          <div className='absolute w-[calc(100%-2rem)] bottom-4 left-4 bg-[var(--background-color-primary)] z-20 flex justify-between items-end p-2 min-h-[65px]'>
                            <div className='!text-sm !text-white h-full min-h-[50px]'>
                              {solucion.nombre}
                            </div>
                            <div className='flex flex-col justify-end'>
                              <button className='!rounded-full !border !border-white w-5 h-5 flex justify-center items-center !text-white group-hover:bg-white group-hover:!text-[var(--text-color-primary)] transition duration-500 ease-initial'>
                                <i className='fas fa-chevron-right text-[4px] font-[100]'></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </NavLink>
                    </div>
                  }
                </>
            ))}
          </Slider>
        </div>
        <div className='hidden md:grid w-full grid-cols-12 gap-y-5 gap-x-6'>
          {
            cargando.soluciones ?

              Array.from({ length: 16 }).map((_:  any, i: number) => <ImageSkeleton className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 h-56" key={i} />)

              :

              soluciones.map((solucion, index) => {
                return (
                  <>
                    {solucion.externo && (
                      <a key={index} href={solucion.url} target="_blank" rel="noopener noreferrer" className='w-full min-h-[280px] col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 block z-10 group'>
                        <div className={classNames('h-full bg-cover relative', {
                          [`bg-[url(${solucion.portada?.url})]`]: solucion.nombre
                        })}>
                          <div className='absolute w-[calc(100%-2rem)] bottom-4 left-4 bg-[var(--background-color-primary)] z-20 flex justify-between items-end p-2 min-h-[65px]'>
                            <div className='!text-white h-full min-h-[50px]'>
                              {solucion.nombre}
                            </div>
                            <div className='flex flex-col justify-end'>
                              <button className='!rounded-full !border !border-white w-5 h-5 flex justify-center items-center !text-white group-hover:bg-white group-hover:!text-[var(--text-color-primary)] transition duration-500 ease-initial'>
                                <i className='fas fa-chevron-right text-[4px] font-[100]'></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </a>
                    )}

                    {!solucion.externo && (
                      <NavLink key={index} to={`/soluciones/${solucion.url}`} className='w-full min-h-[280px] col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 block z-10 group'>
                        <div className={classNames('h-full bg-cover relative', {
                          [`bg-[url(${solucion.portada?.url})]`]: solucion.nombre
                        })}>
                          <div className='absolute w-[calc(100%-2rem)] bottom-4 left-4 bg-[var(--background-color-primary)] z-20 flex justify-between items-end p-2 min-h-[65px]'>
                            <div className='!text-white h-full min-h-[50px]'>
                              {solucion.nombre}
                            </div>
                            <div className='flex flex-col justify-end'>
                              <button className='!rounded-full !border !border-white w-5 h-5 flex justify-center items-center !text-white group-hover:bg-white group-hover:!text-[var(--text-color-primary)] transition duration-500 ease-initial'>
                                <i className='fas fa-chevron-right text-[4px] font-[100]'></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </NavLink>
                    )}
                  </>
                ) 
              })
          }
        </div>
      </section>
      {/* Industrias */}
      <section className='px-4 mt-12 md:mt-0 md:px-10 lg:px-36 xl:px-[175px] 2xl:px-[400px] pb-16 lg:pb-24'>
        <div className='pb-4 lg:pb-10'>
          <h1 className="text-[2rem] lg:text-[2.5rem] font-bold text-center leading-9 lg:leading-[48px] text-[var(--text-color-secondary)]">Industrias donde estamos presentes</h1>
        </div>
        <div className="block md:hidden">
          <Slider
            dots={true}
            infinite={industrias.length > 2}
            arrows={false}
            speed={500}
            slidesToShow={2}
            slidesToScroll={1}
            className='!w-full !h-full'
          >
            {industrias.map((industria) => (

              cargando.industrias ?

                <div key={industria.id} className="px-2">
                  <ImageSkeleton className="w-full h-[300px] rounded-lg" />
                </div>

                :

                <div key={industria.id} className="px-2">
                  <NavLink to={industria.slug} className='w-full h-[300px] block z-10 group'>
                    <div className='h-full bg-[#f0f0f0] text-[#204199] flex flex-col justify-between items-center !rounded-lg !text-center group-hover:!bg-[#0E67C2] group-hover:!text-white transition duration-500 ease-in-out'>
                      <div className='w-full h-full flex justify-center items-center'>
                        <img src={industria.icono?.url} alt="Industria 1" className='max-w-full max-h-full object-contain scale-100 group-hover:scale-125 transition duration-700 group-hover:brightness-0 group-hover:invert-100' />
                      </div>
                      <h2 className='w-full h-1/2 text-center !text-[19.2px] !leading-[23px] md:!text-[21.6px] md:!leading-[25.92px] xl:!text-[24px] xl:!leading-[28.8px] px-4'>
                        {industria.nombre}
                      </h2>
                      <div className='w-full h-full flex justify-center items-center'>
                        <button className='!rounded-full !border-2 !border-[#204199] w-8 h-8 flex justify-center items-center !text-[#204199] group-hover:!text-[#f0f0f0] group-hover:bg-[#0E67C2] group-hover:!border-[#f0f0f0] transition duration-500 ease-in-out'>
                          <i className='fas fa-chevron-right text-xs font-[100]'></i>
                        </button>
                      </div>
                    </div>
                  </NavLink>
                </div>
            ))}
          </Slider>
        </div>
        <div className='hidden md:grid w-full grid-cols-12 gap-y-5 gap-x-6'>
          {
            cargando.industrias ?

              Array.from({ length: 8 }).map((_: any, i: number) => <CardSkeleton className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3" key={i} />)

              :

              industrias.map((industria) => (
                <NavLink key={industria.id} to={industria.slug} className='w-full h-[300px] col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 !rounded-lg !bg-[#f0f0f0] !text-[#204199] group hover:!bg-[#0E67C2] hover:!text-white transition duration-500 ease-in-out'>
                  <div className='flex flex-col h-full justify-between items-center'>
                    <div className='w-full h-full flex justify-center items-center'>
                      <img src={industria.icono?.url} alt="Industria 1" className='max-w-full max-h-full object-contain scale-75 group-hover:scale-125 transition duration-700 group-hover:brightness-0 group-hover:invert-100' />
                    </div>
                    <h2 className='w-full h-1/2 text-center text-2xl px-4'>
                      {industria.nombre}
                    </h2>
                    <div className='w-full h-full flex justify-center items-center'>
                      <button className='!rounded-full !border-2 !border-[#204199] w-8 h-8 flex justify-center items-center !text-[#204199] group-hover:!text-[#f0f0f0] group-hover:bg-[#0E67C2] group-hover:!border-[#f0f0f0] transition duration-500 ease-initial'>
                        <i className='fas fa-chevron-right text-xs font-[100]'></i>
                      </button>
                    </div>
                  </div>
                </NavLink>
              ))
          }
        </div>
      </section>
      {/* Casos de éxito */}
      <section className='px-4 md:px-10 lg:px-36 xl:px-[175px] 2xl:px-[400px] pb-32 lg:pb-24'>
        <div className="h-full flex flex-col md:flex-row justify-between items-start gap-4">
          <div className='w-full md:w-1/3 h-full'>
            <div>
              <h2 className="text-center md:text-left text-[2rem] md:text-[2.5rem] font-bold leading-9 md:leading-[48px] text-[var(--text-color-secondary)] leading-12 !mt-0">Casos de éxito</h2>
              <p className='text-center md:text-left text-[1rem] md:text-[1.25rem] leading-6 md:leading-8 text-[var(--text-color-primary)] py-3'>
                Descubre cómo ha sido el camino del éxito de nuestros clientes en muchas industrias.
              </p>
            </div>
          </div>
          <div className="w-full md:w-2/3 relative">
            <Slider
              ref={sliderCasosRef}
              dots={false}
              infinite={casosExito.length > 2}
              arrows={false}
              speed={500}
              slidesToShow={isMobile ? 1 : 2}
              slidesToScroll={1}
              className='!w-full !h-full'
              afterChange={(index) => {
                setActualCaso(index + 1);
              }}
            >
              {
                cargando.casosExito ?

                  Array.from({ length: 6 }).map((_: any, i: number) => <CardSkeleton className="px-2" key={i} />)

                  :

                  casosExito.map((caso, index) => (
                    <div key={caso.id} className='px-2'>
                      <div className={classNames('relative min-h-[360px] bg-size-[auto_100%] bg-[50%] hover:bg-size-[auto_120%] transition hover:bg-[70%] p-4 ease-out transition-all duration-1000', {
                        [`bg-[url(${caso.portadaCarousel?.url})]`]: caso.portadaCarousel?.url
                      })}>
                        <div className='h-full'>
                          <h4 className='!text-white !text-[30px] !font-[500] !leading-[36px] !mt-0 !mx-0 !mb-[10px] !p-0'>{caso.nombre}</h4>
                          <small className='!text-white !text-[0.875rem]'><i className="bi bi-geo-alt-fill pr-1"></i>{caso.provincia}, {caso.region}</small>
                        </div>
                        <div className='absolute bottom-4 left-4 z-20 flex justify-between items-start gap-4 p-2'>
                          <NavLink to={`/casos-de-exito/${caso.slug}`} className="!text-white underline text-[20px] underline-offset-8">
                            Ir al artículo
                          </NavLink>
                          <button className='!rounded-full !border !border-white w-7 h-7 flex justify-center items-center !text-white transition duration-500 ease-initial'>
                            <i className='fas fa-chevron-right text-[6px] font-[100]'></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
              }
            </Slider>
            <div className='absolute -bottom-14 md:bottom-0 right-2 md:-left-[30rem] lg:-left-[36rem] xl:-left-[49rem] flex justify-end items-center gap-6 mt-auto w-full self-end'>
              <div>
                <span className='font-bold text-[var(--text-color-primary)]'>
                  {actualCaso.toString().padStart(2, '0')}
                </span>
                /
                <span className='text-[var(--text-color-primary)]'>
                  {casosExito.length.toString().padStart(2, '0')}
                </span>
              </div>
              <div className='flex items-center gap-3'>
                <button
                  className='!rounded-full !border-2 !border-[var(--text-color-tertiary)] w-10 h-10 flex justify-center items-center !text-[var(--text-color-primary)] transition duration-500 ease-initial'
                  onClick={handleCasoAnterior}
                >
                  <i className='fas fa-chevron-left text-[12px] font-[700]'></i>
                </button>
                <button
                  className='!rounded-full !border-2 !border-[var(--text-color-tertiary)] w-10 h-10 flex justify-center items-center !text-[var(--text-color-primary)] transition duration-500 ease-initial'
                  onClick={handleCasoSiguiente}
                >
                  <i className='fas fa-chevron-right text-[12px] font-[700]'></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Contactanos */}
      <section className='px-4 md:px-10 lg:px-36 xl:px-[175px] 2xl:px-[400px] pb-20'>
        <div className='flex flex-col md:flex-row justify-between items-start lg:items-center gap-2'>
          <figure className='hidden md:block w-full !ml-0'>
            <img src={GascoContacto} className='w-full' />
          </figure>
          <div className='w-full'>
            <h2 className='text-[2rem] md:text-[2.25rem] lg:text-[2.5rem] text-center md:text-left leading-9 md:leading-[48px] font-bold text-[var(--text-color-secondary)]'>Contacta con nuestros ejecutivos</h2>
            <p className='text-[1rem] md:text-[1.125rem] lg:text-[1.25rem] text-center md:text-left leading-6 lg:leading-8 text-[var(--text-color-primary)] py-3'>
              Trabajamos para ofrecer soluciones energéticas innovadoras y de acuerdo a las necesidades de cada uno de nuestros clientes.
            </p>
            <div className="flex justify-center">
              <NavLink to="/contacto" className="text-sm md:text-base !rounded-lg !bg-[var(--background-color-primary)] px-2 py-3 !decoration-none !text-white w-full h-full max-w-36 md:max-w-full lg:max-w-64 flex justify-center items-center">
                Contáctanos
              </NavLink>
            </div>
          </div>
        </div>
      </section>
      {/* Podcast */}
      <section className='px-0 md:px-10 lg:px-36 xl:px-[175px] 2xl:px-[400px] pb-72'>
        <div className='!rounded-3xl w-full px-10 md:px-0 lg:px-10 pt-3 pb-6 lg:p-4 bg-[var(--background-color-secondary)] flex flex-col md:flex-row justify-between items-center md:items-start lg:items-center lg:gap-6'>
          <figure className='w-full md:min-w-[192px] md:max-w-[192px] lg:w-1/3'>
            <img src={GascoPodcast} className='w-full h-full' />
          </figure>
          <div className='w-full md:w-full lg:w-2/3 lg:pe-8 md:pt-6 flex flex-col justify-between items-start'>
            <h2 className='text-[2rem] md:text-[2.225rem] !m-0 lg:text-[2.5rem] text-center md:text-left leading-9 md:leading-10 lg:leading-[48px] font-bold text-[var(--text-color-secondary)] pb-5 lg:pb-3'>
              Conoce y escucha nuestro podcast
            </h2>
            <p className='text-[1rem] md:text-[1.125rem] pe-2 text-center md:text-left leading-6 lg:leading-8 text-[var(--text-color-primary)] pb-4'>
              Cuando la energía se orienta hacia el bien común, se vuelve un motor fundamental para construir un futuro mejor para las nueva generaciones. Conversamos con diferentes expertos para abordar la evolución de la energía en diferentes industrias.
            </p>
            <div className='flex md:justify-start justify-center w-full pe-2'>
              <NavLink to="/podcast" className="text-[12.8px] md:text-[14.4px] lg:text-base md:!leading-[14.4px] !rounded-md !bg-[var(--background-color-primary)] px-[40px] md:px-[45px] py-[8px] md:py-[11px] !decoration-none !text-white w-full h-full max-w-[154px] md:max-w-64 flex justify-center items-center">
                Ir al Podcast
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

export default Home;